import { cfetch, BadStatusError } from '@confluence/network';

import { reportError } from '../utils/reportError.ts';
import type { Snippet } from '../types/snippet.ts';
import { SnippetBodyFormat } from '../types/snippetBodyFormat.ts';
import { SnippetProduct } from '../types/snippetProduct.ts';
import { SnippetScope } from '../types/snippetScope.ts';

import {
	type SnippetFavoriteData,
	type SnippetData,
	SnippetError,
	type CreateSnippetPayload,
	type UpdateSnippetPayload,
} from './types/blockServiceTypes.ts';
import { transformSnippet, transformSnippets } from './utils/blockServiceUtils.ts';
import { clearSnippetsCache, withSnippetsCache } from './cache/snippetsCache.ts';

const COMMON_HEADERS = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
};

const cfetchSnippet = async (...args: Parameters<typeof cfetch>): Promise<Response> => {
	try {
		return await cfetch(...args);
	} catch (err) {
		if (BadStatusError.isBadStatusError(err)) {
			const body = await err.response.json().catch(() => ({}));
			const snippetErr = new SnippetError(body?.error ?? err.response.statusText);
			snippetErr.statusCode = err.response.status;
			throw snippetErr;
		}
		throw err;
	}
};

const BLOCK_SERVICE_API_URL = '/gateway/api/blocks/v1';
const SNIPPETS_PATH = '/snippets';

export type FetchSnippetsOptions = {
	isSnippetSharingForSiteEnabled?: boolean;
};

type SnippetSettingsResponse = {
	sharingEnabled: boolean;
};

/**
 * Fetches snippet settings from the Block Service and returns whether sharing for the site is enabled.
 * On non-ok response or parse errors, reports the error and returns false.
 */
export const getIsSnippetSharingForSiteEnabled = async (): Promise<boolean> => {
	try {
		const response = await cfetchSnippet(`${BLOCK_SERVICE_API_URL}${SNIPPETS_PATH}/settings`, {
			method: 'GET',
			headers: COMMON_HEADERS,
		});
		const data: SnippetSettingsResponse = await response.json();
		return data.sharingEnabled;
	} catch (error) {
		reportError(error instanceof Error ? error : new SnippetError(String(error)));
		return false;
	}
};

/**
 * Fetch snippets from the Block Service for Confluence. https://bitbucket.org/atlassian/block-service/src/main/
 * Fetches site configuration first to decide whether to include scope=site, then fetches user/global (and site if enabled).
 * This allows a single cache: the cached result always matches the current config. The config should not change frequently.
 * Please note that no error handling is done. Ensure you are handling bad response codes when using this.
 */
export const fetchSnippets = (options?: FetchSnippetsOptions): Promise<Snippet[]> => {
	return withSnippetsCache(async () => {
		const isSnippetSharingForSiteEnabled =
			options?.isSnippetSharingForSiteEnabled !== undefined
				? options.isSnippetSharingForSiteEnabled
				: await getIsSnippetSharingForSiteEnabled();

		const [userSnippetsResult, globalSnippetsResult, siteSnippetsResult] = await Promise.allSettled(
			[
				fetchSnippetData(SnippetScope.USER),
				fetchSnippetData(SnippetScope.GLOBAL),
				isSnippetSharingForSiteEnabled ? fetchSnippetData(SnippetScope.SITE) : Promise.resolve([]),
			],
		);

		const userSnippets: Snippet[] =
			userSnippetsResult.status === 'fulfilled'
				? await transformSnippets(userSnippetsResult.value)
				: (logResultError(userSnippetsResult.reason, SnippetScope.USER), []);

		const globalSnippets: Snippet[] =
			globalSnippetsResult.status === 'fulfilled'
				? await transformSnippets(globalSnippetsResult.value)
				: (logResultError(globalSnippetsResult.reason, SnippetScope.GLOBAL), []);

		const siteSnippets: Snippet[] =
			siteSnippetsResult.status === 'fulfilled'
				? await transformSnippets(siteSnippetsResult.value)
				: (logResultError(siteSnippetsResult.reason, SnippetScope.SITE), []);

		return [...userSnippets, ...globalSnippets, ...siteSnippets];
	});
};

const fetchSnippetData = async (scope: SnippetScope): Promise<SnippetData[]> => {
	const params = new URLSearchParams({
		format: SnippetBodyFormat.ADF,
		product: SnippetProduct.CONFLUENCE,
		scope,
	});

	const response = await cfetchSnippet(
		`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}?${params.toString()}`,
		{
			method: 'GET',
			headers: COMMON_HEADERS,
		},
	);

	const responseData = await response.json();
	if (!responseData.success) {
		return [];
	}

	// Success and error can both be present, so we need to log the error if it is present
	if (Array.isArray(responseData.error) && responseData.error.length > 0) {
		responseData.error.forEach((error: any) => {
			logResultError(error, scope);
		});
	}

	return responseData.success;
};

export const createSnippet = async (params: CreateSnippetPayload): Promise<Snippet> => {
	const response = await cfetchSnippet(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}`, {
		method: 'POST',
		headers: COMMON_HEADERS,
		body: JSON.stringify(params),
	});

	const responseData: SnippetData = await response.json();
	const snippet = await transformSnippet(responseData);

	// Clear the cache so the newly created snippet appears when fetching snippets again
	// (e.g., when navigating to a different page or when the extension provider rebuilds)
	// Quick insert only adds the new snippet to the current editor instance's menu
	// navigating to a new edit page the extension providers rebuilds and call fetchSnippets which will return the cached data.
	clearSnippetsCache();

	if (snippet) {
		return snippet;
	} else {
		throw new Error('Unable to transform snippet data after creation');
	}
};

/**
 * Delete a snippet via the Block Service API.
 * Passes snippetId as the path segment after /snippets/.
 */
export const deleteSnippet = async (snippetId: string): Promise<void> => {
	await cfetchSnippet(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}/${snippetId}`, {
		method: 'DELETE',
		headers: COMMON_HEADERS,
	});
};

export const updateSnippet = async (
	snippetId: string,
	snippetPayload: UpdateSnippetPayload,
): Promise<Snippet> => {
	const response = await cfetchSnippet(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}/${snippetId}`, {
		method: 'PUT',
		headers: COMMON_HEADERS,
		body: JSON.stringify(snippetPayload),
	});

	const responseData: SnippetData = await response.json();
	const snippet = await transformSnippet(responseData);

	// Clear the cache so the updated snippet appears when fetching snippets again
	// (e.g., when navigating to a different page or when the extension provider rebuilds)
	clearSnippetsCache();

	if (snippet) {
		return snippet;
	} else {
		throw new Error('Unable to transform snippet data after update');
	}
};

export const favoriteSnippet = async (snippetId: string): Promise<SnippetFavoriteData> => {
	const response = await cfetchSnippet(
		`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}/${snippetId}/favorite`,
		{
			method: 'POST',
			headers: COMMON_HEADERS,
		},
	);

	return response.json();
};

export const unfavoriteSnippet = async (snippetId: string): Promise<void> => {
	await cfetchSnippet(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}/${snippetId}/favorite`, {
		method: 'DELETE',
		headers: COMMON_HEADERS,
	});
};

/**
 * Records that a snippet was inserted (increments insertCount on the server).
 * Intended to be called fire-and-forget; errors are reported but not thrown to the caller.
 */
export const incrementSnippetInsertCount = async (snippetId: string): Promise<void> => {
	try {
		await cfetchSnippet(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}/${snippetId}/insertCount`, {
			method: 'PUT',
			headers: COMMON_HEADERS,
		});
	} catch (error) {
		reportError(error instanceof Error ? error : new SnippetError(String(error)));
	}
};

/**
 * Share a snippet with the site (scope SITE). Call after create/update/duplicate when the user selects Shared.
 */
export const shareSnippet = async (snippetId: string): Promise<void> => {
	await cfetchSnippet(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}/${snippetId}/share`, {
		method: 'POST',
		headers: COMMON_HEADERS,
	});

	// Clear the cache so the newly created snippet appears when fetching snippets again
	// (e.g., when navigating to a different page or when the extension provider rebuilds)
	// Quick insert only adds the new snippet to the current editor instance's menu
	// navigating to a new edit page the extension providers rebuilds and call fetchSnippets which will return the cached data.
	// TODO: revisit if clearSnippetsCache is needed for create, update, duplicate, etc.
	clearSnippetsCache();
};

/**
 * Unshare a snippet (revert to scope USER). Call after update when the user selects Private.
 * Uses DELETE on the same share endpoint (v1/snippets/{snippetId}/share).
 */
export const unshareSnippet = async (snippetId: string): Promise<void> => {
	await cfetchSnippet(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}/${snippetId}/share`, {
		method: 'DELETE',
		headers: COMMON_HEADERS,
	});

	clearSnippetsCache();
};

const logResultError = (reason: unknown, scope: SnippetScope): void => {
	const reasonString = reason instanceof Error ? reason.message : JSON.stringify(reason);
	const errorToLog = new Error(`Error when fetching ${scope} scoped snippets: ${reasonString}`);
	reportError(errorToLog);
};
